import {t} from 'i18next';
import {injectable} from 'inversify';
import {makeAutoObservable} from 'mobx';
import {RefObject} from 'react';
import {toast} from 'react-toastify';

import {UIStoreType} from './types';

export const SAVING_STATUS = {
  SAVING: 'saving',
  IDLE: 'idle',
  ERROR: 'error',
} as const;

type SavingStatus = (typeof SAVING_STATUS)[keyof typeof SAVING_STATUS];

@injectable()
export class UIStore implements UIStoreType {
  private dailiesPrintNodeRef: RefObject<HTMLDivElement> | null = null;
  public isLoading = false;
  public savingError: Error | null = null;
  public isSaving: SavingStatus = 'idle';

  constructor() {
    makeAutoObservable(this);
  }

  setDailiesPrintNodeRef(ref: RefObject<HTMLDivElement>) {
    this.dailiesPrintNodeRef = ref;
  }

  get getDailiesPrintNodeRef() {
    return this.dailiesPrintNodeRef;
  }

  setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  setSaving(status: SavingStatus, error?: Error) {
    this.isSaving = status;

    if (status === SAVING_STATUS.ERROR) {
      toast.update('saving-toast', {
        render: error?.message || t('gantt:toast.saving_state.error'),
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    }
  }

  async handleSaveOperation<T>(saveFunction: () => Promise<T>): Promise<T> {
    try {
      this.setSaving(SAVING_STATUS.SAVING);
      const result = await saveFunction();
      this.setSaving(SAVING_STATUS.IDLE);
      return result;
    } catch (error) {
      this.setSaving(SAVING_STATUS.ERROR, error instanceof Error ? error : new Error('Unknown error occurred'));
      throw error;
    }
  }

  showErrorNotification(message: string) {
    toast.error(message);
  }
}
